import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Building2, Hammer } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useLang } from "@/i18n/LangContext";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_84c34016-99d1-4711-81d8-4592c54a5a5e/artifacts/jsnv4v6a_1000368650.jpg";
const CONSTRUCTION_BG = "https://customer-assets.emergentagent.com/job_84c34016-99d1-4711-81d8-4592c54a5a5e/artifacts/glvnzv3i_1000368636.jpg";
const TRAVAUX_BG = "https://images.unsplash.com/photo-1642781100314-f1ac6fb42296?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxkYXJrJTIwY29uc3RydWN0aW9uJTIwc2l0ZSUyMG5pZ2h0fGVufDB8fHx8MTc3NjIwNzQ0Mnww&ixlib=rb-4.1.0&q=85";

export default function LandingPage() {
  const navigate = useNavigate();
  const { t } = useLang();
  const [hovered, setHovered] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 300);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="h-screen w-screen overflow-hidden bg-[#050505] relative" data-testid="landing-page">
      {/* Language Switcher - Top Right */}
      <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-30">
        <LanguageSwitcher />
      </div>

      {/* Center Logo Overlay */}
      <motion.div
        className="absolute inset-0 z-20 flex flex-col items-center justify-center pointer-events-none"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: loaded ? 1 : 0, scale: loaded ? 1 : 0.8 }}
        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
      >
        <img src={LOGO_URL} alt="KMSN Logo" className="w-24 h-24 sm:w-32 sm:h-32 lg:w-40 lg:h-40 object-contain rounded-lg" data-testid="landing-logo" />
        <motion.p
          className="font-heading text-[#C8A951] text-sm sm:text-base lg:text-lg tracking-[0.3em] uppercase mt-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: loaded ? 1 : 0, y: loaded ? 0 : 20 }}
          transition={{ delay: 0.8, duration: 0.8 }}
        >
          {t("slogan")}
        </motion.p>
      </motion.div>

      {/* Split Container */}
      <div className="flex flex-col lg:flex-row h-full w-full">
        {/* KMSN CONSTRUCTION */}
        <motion.div
          className="relative cursor-pointer overflow-hidden"
          data-testid="split-construction-panel"
          style={{ flex: hovered === "construction" ? 1.6 : hovered === "travaux" ? 0.6 : 1 }}
          onMouseEnter={() => setHovered("construction")}
          onMouseLeave={() => setHovered(null)}
          onClick={() => navigate("/construction")}
          initial={{ opacity: 0 }}
          animate={{ opacity: loaded ? 1 : 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          layout
        >
          <motion.div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${CONSTRUCTION_BG})` }} animate={{ scale: hovered === "construction" ? 1.05 : 1 }} transition={{ duration: 0.8 }} />
          <div className={`absolute inset-0 transition-all duration-700 ${hovered === "travaux" ? "bg-black/80" : "bg-black/50"}`} />
          <div className="absolute inset-0 flex flex-col items-center justify-center z-10 p-6">
            <motion.div animate={{ y: hovered === "construction" ? -10 : 0 }} transition={{ duration: 0.5 }} className="flex flex-col items-center">
              <Building2 className="w-10 h-10 lg:w-14 lg:h-14 text-[#C8A951] mb-4" strokeWidth={1} />
              <h2 className="font-heading text-white text-2xl sm:text-3xl lg:text-5xl tracking-tight">KMSN</h2>
              <span className="text-[#C8A951] text-sm sm:text-base lg:text-lg tracking-[0.25em] uppercase mt-1 font-body">{t("construction")}</span>
              <motion.div className="h-[2px] bg-[#C8A951] mt-6" animate={{ width: hovered === "construction" ? 120 : 60 }} transition={{ duration: 0.5 }} />
              <AnimatePresence>
                {hovered === "construction" && (
                  <motion.p className="text-white/70 text-sm mt-4 text-center max-w-xs font-body" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.3 }}>
                    {t("constructionSub")}
                  </motion.p>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
          <div className="absolute right-0 top-0 bottom-0 w-[1px] bg-white/10 hidden lg:block" />
          <div className="absolute left-0 right-0 bottom-0 h-[1px] bg-white/10 lg:hidden" />
        </motion.div>

        {/* KMSN TRAVAUX */}
        <motion.div
          className="relative cursor-pointer overflow-hidden"
          data-testid="split-travaux-panel"
          style={{ flex: hovered === "travaux" ? 1.6 : hovered === "construction" ? 0.6 : 1 }}
          onMouseEnter={() => setHovered("travaux")}
          onMouseLeave={() => setHovered(null)}
          onClick={() => navigate("/travaux")}
          initial={{ opacity: 0 }}
          animate={{ opacity: loaded ? 1 : 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          layout
        >
          <motion.div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${TRAVAUX_BG})` }} animate={{ scale: hovered === "travaux" ? 1.05 : 1 }} transition={{ duration: 0.8 }} />
          <div className={`absolute inset-0 transition-all duration-700 ${hovered === "construction" ? "bg-black/80" : "bg-black/50"}`} />
          <div className="absolute inset-0 flex flex-col items-center justify-center z-10 p-6">
            <motion.div animate={{ y: hovered === "travaux" ? -10 : 0 }} transition={{ duration: 0.5 }} className="flex flex-col items-center">
              <Hammer className="w-10 h-10 lg:w-14 lg:h-14 text-[#C8A951] mb-4" strokeWidth={1} />
              <h2 className="font-heading text-white text-2xl sm:text-3xl lg:text-5xl tracking-tight">KMSN</h2>
              <span className="text-[#C8A951] text-sm sm:text-base lg:text-lg tracking-[0.25em] uppercase mt-1 font-body">{t("travaux")}</span>
              <motion.div className="h-[2px] bg-[#C8A951] mt-6" animate={{ width: hovered === "travaux" ? 120 : 60 }} transition={{ duration: 0.5 }} />
              <AnimatePresence>
                {hovered === "travaux" && (
                  <motion.p className="text-white/70 text-sm mt-4 text-center max-w-xs font-body" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.3 }}>
                    {t("travauxSub")}
                  </motion.p>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
